import type {ComponentType} from 'react';
import {useEffect} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router-dom';
import type {
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {RoleEnum, UserStatusEnum} from 'src/api/ProactApiClient';

/* static */

import {runBootJanitor} from 'src/offline/sessionJanitor';
import {
  isSyncSchedulerStarted,
  requestDrain,
  startSyncScheduler,
} from 'src/offline/syncScheduler';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {getProjectScopeOptions} from 'src/redux/slices/projectScopeSlice';
import {
  getUserInstances,
  setIsSessionInitiailized,
  setIsTenantInitiailized,
  validateUserSession,
} from 'src/redux/slices/sessionSlice';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {useIsLoggedIn} from 'src/shared/auth/useIsLoggedIn';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useIdleTimeout from 'src/shared/hooks/useIdleTimeout';
import {useVersionCheck} from 'src/shared/hooks/useVersionCheck';

export interface PrivateOutletProps {
  roles?: RoleEnum[];
  module?: ModuleEnum;
  operation?: PermissionOperationEnum;
  operationName?: string;
  allowGlobalAdminBypass?: boolean;
  requireSuperAdmin?: boolean;
  LoadingPage: ComponentType<{title?: string; message?: string}>;
}

export const PrivateOutlet = ({
  roles,
  module,
  operation,
  operationName,
  allowGlobalAdminBypass,
  requireSuperAdmin,
  LoadingPage,
}: PrivateOutletProps) => {
  useIdleTimeout();
  useVersionCheck();

  // App-lifetime offline runtime: clean up stale sessions once, then start the
  // drain scheduler (module singleton, never stopped on unmount).
  useEffect(() => {
    if (isSyncSchedulerStarted()) return;
    void runBootJanitor().finally(startSyncScheduler);
  }, []);

  const location = useLocation();
  const {isLoggedIn, isLoading: isLoginLoading} = useIsLoggedIn();
  const {
    session,
    isSessionLoading,
    isSessionInitialized,
    isTenantInitialized,
    isSuperAdmin,
  } = useAppSelector(state => state.session);
  const user = session?.user;
  const identityUserId = useAppSelector(state => state.session.user?.id);
  const identityInstanceId = useAppSelector(
    state => state.session.instance?.id
  );

  // The boot drain above can run before redux identity settles and no-op (owned
  // filter); re-poke once identity lands so pending items don't wait 60s.
  useEffect(() => {
    if (identityUserId != null && identityInstanceId != null) requestDrain();
  }, [identityUserId, identityInstanceId]);

  const {hasPermission} = useHasModulePermission({
    module: module,
    operation: operation,
    operationName: operationName,
  });

  const dispatch = useAppDispatch();

  // Reload scope options on every boot and instance change; the tenant-init
  // branch below runs once per persisted session only, so it cannot own this.
  useEffect(() => {
    if (identityUserId != null && identityInstanceId != null) {
      void dispatch(getProjectScopeOptions());
    }
  }, [dispatch, identityUserId, identityInstanceId]);

  useEffect(() => {
    if (
      isLoggedIn === true &&
      isLoginLoading === false &&
      isSessionInitialized === false
    ) {
      console.log('Session not initialized. Initializing session.');
      dispatch(setIsSessionInitiailized(true));
      void dispatch(validateUserSession());
    }

    if (
      isSessionInitialized === true &&
      isTenantInitialized === false &&
      session?.userStatus !== UserStatusEnum.Inactive &&
      session?.isCognitoUserMatched === true
    ) {
      console.log('Tenant not initialized. Initializing tenant.');
      dispatch(setIsTenantInitiailized(true));
      void dispatch(getUserInstances());
    }
  }, [
    isLoggedIn,
    isLoginLoading,
    isSessionInitialized,
    session?.isCognitoUserMatched,
    session?.userStatus,
  ]);

  if (isLoginLoading === true) {
    return <LoadingPage title="Loading your account..." />;
  }

  if (isLoggedIn === false) {
    const returnUrl = location.pathname + location.search;
    const loginUrl = `${PathUtility.Login()}?returnUrl=${encodeURIComponent(
      returnUrl
    )}`;
    return <Navigate to={loginUrl} />;
  }

  if (isSessionLoading === true || isSessionInitialized === false) {
    return <LoadingPage title="Loading your user session..." />;
  }

  if (session?.userStatus === UserStatusEnum.Inactive) {
    return <Navigate to={PathUtility.InactiveUser()} />;
  }

  if (session?.isCognitoUserMatched === true && isTenantInitialized === false) {
    return <LoadingPage title="Loading your user session..." />;
  }

  if (
    !isSuperAdmin &&
    session?.userStatus === UserStatusEnum.Pending &&
    !location.pathname.includes('/dashboard/training')
  ) {
    return <Navigate to={PathUtility.TrainingDashboard('user')} />;
  }

  if (session?.isCognitoUserMatched === false) {
    return <Navigate to={PathUtility.PermissionDenied()} />;
  }

  if (session?.user?.roleId === RoleEnum.NotificationOnly) {
    return <Navigate to={PathUtility.PermissionDenied()} />;
  }

  // Super-admin-only routes
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to={PathUtility.PermissionDenied()} />;
  }

  // First, we check if the user is in any required roles.
  if (
    !isSuperAdmin &&
    user &&
    user.roleId &&
    roles?.some(role => user.roleId === role) === false
  ) {
    return <Navigate to={PathUtility.PermissionDenied()} />;
  }

  const bypassed = allowGlobalAdminBypass && isSuperAdmin;

  // Second, we check if the user has the required permissions given the module and operation.
  if (module && operation && !hasPermission && !bypassed) {
    return <Navigate to={PathUtility.PermissionDenied()} />;
  }

  return <Outlet />;
};
